import * as React from "react";
import { useTranslation } from "react-i18next";
import { Box, useTheme } from "@mui/material";
import { Badge } from "@/lib/components/Badge";
import { OverviewTableI18N } from "@/translations";

export interface UnreadTotalHeaderProps {
    /** The plain column label, e.g. "Info". */
    label: string;
    /** Sum of unread messages over the whole filtered set (`totalUnreadMessages`, #1604 Phase 5.1/5.2), not
     *  just the current page. */
    total: number;
}

/**
 * Shared Info-column header for the request and order overview tables (#1604 Phase 5.4, corrected in Phase
 * 5.5): the column label plus a counter pill with `total`, the sum of unread messages over the whole
 * filtered set. Renders nothing but the label at 0, mirroring the per-row unread badge
 * (`RequestInfoCell.tsx`, `OrderTrackingTable.tsx#renderUnreadBadgeCell`), which likewise returns no badge
 * at 0. This is also what keeps the counter away from an Innendienst-Mitarbeiter without an explicit role
 * check: the backend never returns a total above 0 for that role (`RequestService`/`OrderService`), so no
 * role check is needed here.
 *
 * Renders `@/lib/components/Badge`, the project's own inline pill, instead of MUI's `Badge`: MUI's `Badge`
 * positions its counter absolutely against an anchor child (`transform: translate(50%, -50%)` at the
 * anchor's top-right corner), and this header has no real anchor to give it, the label is plain text and a
 * zero-size anchor makes the counter float onto whatever content sits beside it (Phase 5.4's defect, the
 * counter overlapping the "Info" label and the dropdown caret). The pill instead occupies its own sized box
 * inline with the label, the sort arrow and the caret, so it covers no glyph of either neighbour.
 *
 * Deliberately carries no chat-bubble icon, unlike the per-row badge: this header total and a row's own
 * unread count answer different questions (whole filtered set vs. one row), and dropping the icon here
 * keeps the two from reading as the same indicator at a glance. It is also visually distinct from the
 * column's own filter indicator (`DataTableColumnHeader`'s small `FilterAlt` icon), a colored counter pill
 * versus a plain outline icon.
 */
export const UnreadTotalHeader: React.FC<UnreadTotalHeaderProps> = ({ label, total }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    if (total <= 0) {
        return <>{label}</>;
    }

    const tooltipTitle = t(OverviewTableI18N.tooltip.unreadMessagesTotal, { count: total });

    return (
        <Box display="inline-flex" alignItems="center" gap="4px">
            {label}
            {/* DataTableColumnHeader wraps the whole header content in its dropdown-trigger button
                (DataTableColumnHeader.tsx), so a click here bubbles into that button's onClick and opens
                the dropdown unless it is stopped in the capture phase. onClickCapture, not onClick: the
                span never receives focus, so jsx-a11y/click-events-have-key-events would demand a dead
                keyboard handler for an onClick it does not require for onClickCapture. */}
            <span onClickCapture={(event) => event.stopPropagation()} aria-label={tooltipTitle}>
                <Badge label={String(total)} backgroundColor={theme.palette.error.main} tooltip={tooltipTitle} />
            </span>
        </Box>
    );
};
